import { useState } from 'react';
import { addDays, todayIn } from '@njpos/core';
import { api, usd } from '../lib/api';
import DateRange, { downloadCsv, thisMonth, type Range } from '../components/DateRange';
import { ErrorNote, useLoad } from '../components/ui';

const fmt = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
const bold = { fontWeight: 700 } as const;

type Line = { code: string; name: string; cents: number };
function Section({ title, rows, total, totalLabel }: { title: string; rows: Line[]; total?: number; totalLabel?: string }) {
  return (
    <>
      <tr><td colSpan={2} style={{ ...bold, paddingTop: 14 }}>{title}</td></tr>
      {rows.map((r) => <tr key={r.code}><td style={{ paddingLeft: 24 }}>{r.name}</td><td className="n">{usd(r.cents)}</td></tr>)}
      {rows.length === 0 && <tr><td style={{ paddingLeft: 24 }} className="faint">None</td><td /></tr>}
      {total != null && <tr><td style={bold}>{totalLabel ?? `Total ${title.toLowerCase()}`}</td><td className="n" style={bold}>{usd(total)}</td></tr>}
    </>
  );
}

export function ProfitLoss() {
  const [range, setRange] = useState<Range>(thisMonth());
  const r = useLoad(() => api<any>(`/api/accounting/reports/profit-loss?from=${range.from}&to=${range.to}`), [range.from, range.to]);
  const d = r.data;
  return (
    <div className="stack">
      <div className="row wrap" style={{ gap: 12, alignItems: 'end' }}>
        <DateRange value={range} onChange={setRange} />
        {d && <button onClick={() => downloadCsv(`profit-and-loss-${d.from}-to-${d.to}.csv`, [['Profit and loss', `${d.from} to ${d.to}`], [], ['Income'], ...d.income.map((x: Line) => [x.name, x.cents / 100]), ['Total income', d.total_income_cents / 100], [], ['Cost of goods'], ...d.costs.map((x: Line) => [x.name, x.cents / 100]), ['Gross profit', d.gross_profit_cents / 100], [], ['Expenses'], ...d.expenses.map((x: Line) => [x.name, x.cents / 100]), ['Total expenses', d.total_expenses_cents / 100], [], ['Net income', d.net_income_cents / 100]])}>Download CSV</button>}
      </div>
      <ErrorNote error={r.error} />
      {d && (
        <div className="panel"><div className="panel-body"><h2 style={{ margin: 0 }}>Profit and loss</h2><div className="faint">{fmt(d.from)} to {fmt(d.to)}</div>
          <table className="grid" style={{ maxWidth: 640 }}><tbody>
            <Section title="Income" rows={d.income} total={d.total_income_cents} />
            <Section title="Cost of goods sold" rows={d.costs} total={d.total_costs_cents} totalLabel="Total cost of goods" />
            <tr><td style={{ ...bold, paddingTop: 12 }}>Gross profit</td><td className="n" style={{ ...bold, paddingTop: 12 }}>{usd(d.gross_profit_cents)}</td></tr>
            <Section title="Expenses" rows={d.expenses} total={d.total_expenses_cents} />
            <tr><td style={{ ...bold, fontSize: 18, paddingTop: 14 }}>Net income</td><td className="n" style={{ ...bold, fontSize: 18, paddingTop: 14, color: d.net_income_cents < 0 ? 'var(--bad)' : undefined }}>{usd(d.net_income_cents)}</td></tr>
          </tbody></table></div></div>)}
    </div>
  );
}

export function BalanceSheet() {
  const [asOf, setAsOf] = useState(todayIn());
  const r = useLoad(() => api<any>(`/api/accounting/reports/balance-sheet?asOf=${asOf}`), [asOf]);
  const d = r.data;
  return (
    <div className="stack">
      <div className="row wrap" style={{ gap: 12, alignItems: 'end' }}>
        <label>As of<input type="date" value={asOf} onChange={(e) => e.target.value && setAsOf(e.target.value)} /></label>
        {d && <button onClick={() => downloadCsv(`balance-sheet-${d.as_of}.csv`, [['Balance sheet', d.as_of], [], ['Assets'], ...d.assets.map((x: Line) => [x.name, x.cents / 100]), ['Total assets', d.total_assets_cents / 100], [], ['Liabilities'], ...d.liabilities.map((x: Line) => [x.name, x.cents / 100]), ['Total liabilities', d.total_liabilities_cents / 100], [], ['Equity'], ...d.equity.map((x: Line) => [x.name, x.cents / 100]), ['Net income to date', d.earnings_cents / 100], ['Total equity', d.total_equity_cents / 100]])}>Download CSV</button>}
      </div>
      <ErrorNote error={r.error} />
      {d && (
        <div className="panel"><div className="panel-body"><h2 style={{ margin: 0 }}>Balance sheet</h2><div className="faint">As of {fmt(d.as_of)}</div>
          <table className="grid" style={{ maxWidth: 640 }}><tbody>
            <Section title="Assets" rows={d.assets} total={d.total_assets_cents} />
            <Section title="Liabilities" rows={d.liabilities} total={d.total_liabilities_cents} />
            <Section title="Equity" rows={[...d.equity, { code: 'ni', name: 'Net income to date', cents: d.earnings_cents }]} total={d.total_equity_cents} />
            <tr><td style={{ ...bold, paddingTop: 12 }}>Liabilities and equity</td><td className="n" style={{ ...bold, paddingTop: 12 }}>{usd(d.total_liabilities_cents + d.total_equity_cents)}</td></tr>
          </tbody></table>
          {!d.balanced && <div className="notice bad">The two sides do not match. Tell support: something in the books is out of balance.</div>}
        </div></div>)}
    </div>
  );
}

export function GeneralLedger() {
  const [range, setRange] = useState<Range>(thisMonth());
  const r = useLoad(() => api<any[]>(`/api/accounting/reports/ledger?from=${range.from}&to=${range.to}`), [range.from, range.to]);
  const rows = r.data ?? [];
  return (
    <div className="stack">
      <div className="row wrap" style={{ gap: 12, alignItems: 'end' }}>
        <DateRange value={range} onChange={setRange} />
        <button onClick={() => downloadCsv(`journal-${range.from}-to-${range.to}.csv`, [['Date', 'Entry', 'Account', 'Debit', 'Credit'], ...rows.flatMap((e) => e.lines.map((l: any) => [e.date, e.memo, `${l.account_code} ${l.name}`, l.debit_cents / 100, l.credit_cents / 100]))])}>Download CSV</button>
      </div>
      <ErrorNote error={r.error} />
      <div className="panel"><div className="table-wrap"><table className="grid">
        <thead><tr><th>Date</th><th>Entry</th><th>Account</th><th className="n">Debit</th><th className="n">Credit</th></tr></thead>
        <tbody>{rows.map((e) => e.lines.map((l: any, i: number) => (
          <tr key={`${e.id}${i}`} style={i === 0 ? { borderTop: '2px solid var(--line)' } : undefined}>
            <td>{i === 0 ? fmt(e.date) : ''}</td><td>{i === 0 ? e.memo : ''}</td><td style={i > 0 && l.credit_cents ? { paddingLeft: 24 } : undefined}>{l.name}</td>
            <td className="n">{l.debit_cents ? usd(l.debit_cents) : ''}</td><td className="n">{l.credit_cents ? usd(l.credit_cents) : ''}</td></tr>)))}</tbody>
      </table>{r.data && rows.length === 0 && <div className="empty">No entries in these dates.</div>}</div></div>
      {rows.length >= 300 && <div className="faint">Showing the latest 300 entries. Narrow the dates to see the rest.</div>}
    </div>
  );
}

export function TrialBalance() {
  const [asOf, setAsOf] = useState(todayIn());
  const r = useLoad(() => api<any[]>(`/api/reports/trial-balance?asOf=${addDays(asOf, 0)}`), [asOf]);
  const rows = (r.data ?? []).filter((x) => x.debit_cents || x.credit_cents);
  const d = rows.reduce((s, x) => s + x.debit_cents, 0); const c = rows.reduce((s, x) => s + x.credit_cents, 0);
  return (
    <div className="stack">
      <div className="row wrap" style={{ gap: 12, alignItems: 'end' }}>
        <label>As of<input type="date" value={asOf} onChange={(e) => e.target.value && setAsOf(e.target.value)} /></label>
        <button onClick={() => downloadCsv(`trial-balance-${asOf}.csv`, [['Code', 'Account', 'Debit', 'Credit'], ...rows.map((x) => [x.code, x.name, x.debit_cents / 100, x.credit_cents / 100])])}>Download CSV</button>
      </div>
      <ErrorNote error={r.error} />
      <div className="panel"><div className="table-wrap"><table className="grid">
        <thead><tr><th>Code</th><th>Account</th><th className="n">Debit</th><th className="n">Credit</th></tr></thead>
        <tbody>{rows.map((x) => <tr key={x.code}><td>{x.code}</td><td>{x.name}</td><td className="n">{x.debit_cents ? usd(x.debit_cents) : ''}</td><td className="n">{x.credit_cents ? usd(x.credit_cents) : ''}</td></tr>)}</tbody>
        <tfoot><tr><td /><td style={{ padding: 10, ...bold }}>Total</td><td className="n" style={{ padding: 10, ...bold }}>{usd(d)}</td><td className="n" style={{ padding: 10, ...bold }}>{usd(c)}</td></tr></tfoot>
      </table></div></div>
      {d !== c && <div className="notice bad">Debits and credits do not match.</div>}
    </div>
  );
}
